const SEPARATOR = ';'
const DEFAULT_PAGINATION_KEY = 'pagination_page'
export const DEFAULT_PAGE_SIZE = 30

class Query {
  #params = {}

  constructor(route) {
    this.#params = route?.query ?? {}
  }

  getSearchParams() {
    return this.#params
  }

  getSearchParam(key) {
    return this.#params[key] ?? null
  }

  getPaginationPage(paginationKey = DEFAULT_PAGINATION_KEY) {
    return +this.getSearchParam(paginationKey) || 1
  }

  setPaginationPage(page, paginationKey = DEFAULT_PAGINATION_KEY) {
    return this.setSearchParam(paginationKey, page, false)
  }

  setSearchParam(key, value, append = false) {
    if (append && this.#params[key]) {
      this.#params = {
        ...this.#params,
        [key]: `${this.#params[key]}${SEPARATOR}${value}`
      }
      return this
    }
    this.#params = { ...this.#params, [key]: value }
    return this
  }

  clearSearchParams() {
    this.#params = {}
    return this
  }

  toV2Params() {
    const result = new URLSearchParams()
    for (const [key, rawValue] of Object.entries(this.#params)) {
      const value = String(rawValue)
      if (key === DEFAULT_PAGINATION_KEY) {
        result.set('page', value)
      } else if (key === 'sort') {
        _applySortParam(result, value, this.#params.direction)
      } else if (key === 'direction') {
        // owned by _applySortParam; skip to avoid double-write
      } else {
        result.set(key, value)
      }
    }
    if (!result.has('page')) result.set('page', '1')
    if (!result.has('size')) result.set('size', String(DEFAULT_PAGE_SIZE))
    return result
      .toString()
      .replaceAll('%5B', '[')
      .replaceAll('%5D', ']')
      .replaceAll('%3B', ';')
  }

  removeSearchParam(key, toDelete = null) {
    const accessor = this.#params[key] + ''
    if (!accessor) return this

    if (toDelete && accessor.split(SEPARATOR).length > 1) {
      this.#params = {
        ...this.#params,
        [key]: _sanitizeParam(accessor, toDelete)
      }
      return this
    }

    const params = { ...this.#params }
    delete params[key]
    this.#params = params
    return this
  }
}

// Mirror of the BE sort catalog (ProductSearchService::SORT_OPTIONS in
// platform-api-v2). `default` is the direction sent when the FE value is the
// bare key (no `_<direction>` suffix) — kept in sync with the BE's
// `allowed_directions[0]`.
const SORT_CATALOG = {
  popularity: { default: 'desc', allowed: ['desc'] },
  price: { default: 'asc', allowed: ['asc', 'desc'] },
  new: { default: 'desc', allowed: ['desc'] },
  on_sale: { default: 'desc', allowed: ['desc'] },
  customer_rating: { default: 'desc', allowed: ['desc'] },
  name: { default: 'asc', allowed: ['asc', 'desc'] },
  deals: { default: 'desc', allowed: ['desc'] },
  deals_priority: { default: 'desc', allowed: ['desc'] },
  relevancy: { default: 'desc', allowed: ['desc'] },
  newest_wines_first: { default: 'desc', allowed: ['desc'] }
}

// Parses a dropdown value like `price`, `price_desc`, or `popularity` into
// `[key, direction]`. Returns `null` if the key isn't in the catalog (unknown
// or legacy URL value) so the caller can drop the sort and let the BE default.
const _parseSortValue = (value) => {
  if (SORT_CATALOG[value]) return [value, SORT_CATALOG[value].default]
  const underscore = value.lastIndexOf('_')
  if (underscore === -1) return null
  const key = value.slice(0, underscore)
  const direction = value.slice(underscore + 1)
  if (!SORT_CATALOG[key]?.allowed.includes(direction)) return null
  return [key, direction]
}

const _applySortParam = (result, value, existingDirection) => {
  const parsed = _parseSortValue(value)
  if (!parsed) return
  const [key, defaultDirection] = parsed

  result.set('default_sort', key)
  const direction =
    existingDirection && SORT_CATALOG[key].allowed.includes(existingDirection)
      ? existingDirection
      : defaultDirection
  result.set('direction', direction)

  if (key === 'customer_rating') {
    // exclude products whose hearts are hidden by MIN_RATING_COUNT (< 5 reviews)
    result.set('customer_rating_count[gte]', '5')
  }
}

/*
 * filters and combines attributes into a single string val1;val2
 * ('val1;val2;val3', 'val2') => 'val1;val3'
 */
const _sanitizeParam = (param, toDelete) => {
  const check = `${toDelete}`

  const values = param.split(SEPARATOR).filter((val) => val !== check)
  return values.join(SEPARATOR)
}

export default Query
